import {makeAutoObservable, runInAction} from 'mobx';
import {
    IIntegrationDeleteInfo,
    IIntegrationPreset,
    IShippingConnection,
    SHIPPING_CONNECTION_CODE,
    SHIPPING_INTEGRATION_CATEGORY
} from '../../../../models/IntegrationsTypes';

import {rootContainer, TYPE} from '../../../../services/container/root';
import ShippingIntegrationsService from '../../../../services/api/shipping-integrarions-service';
import {ICredentialsStore, IIntegrationStore, INTEGRATION_STORE_TYPE} from './integration-store';
import ApiKeyCredentialsStore from './credentials/shipping/api-key-credentials-store';
import CustomerNumberCredentialsStore from './credentials/shipping/customer-number-credentials-store';
import IntersoftCredentialsStore from './credentials/shipping/intersoft-credentials-store';
import WebshipperCredentialsStore from './credentials/shipping/webshipper-credentials-store';
import MondialRelayCredentialsStore from './credentials/shipping/mondial-relay-credentials-store';
import ReboundCredentialsStore from './credentials/shipping/rebound-credentials-store';
import DhlCredentialsStore from "./credentials/shipping/dhl-credentials-store";
import CanadaPostOauthCredentialsStore from "./credentials/shipping/canada-post/canada-post-oauth-credentials-store";
import SendcloudReturnsCredentialsStore from "./credentials/shipping/sendcloud-returns-credentials-store";
import FedexCredentialsStore from "./credentials/shipping/fedex-credentials-store";
import FedexV2CredentialsStore from "./credentials/shipping/fedex-v2/fedex-v2-credentials-store";
import NoCredentialsStore from "./credentials/shipping/no-credentials-store";
import EnvoiDuNetCredentialsStore from "./credentials/shipping/edn-credentials-store";
import ApiSecretCredentialsStore from "./credentials/shipping/api-secret-credentials-store";
import LabelGOCredentialsStore from "./credentials/shipping/labelgo-credentials-store";
import YodelCredentialsStore from "./credentials/shipping/yodel-credentials-store";
import SalasaCredentialsStore from "./credentials/shipping/salasa-credentials-store";
import DhlDeReturnsCredentialsStore from './credentials/shipping/dhl-de-returns-credentials-store';
import EasyshipCredentialsStore from "./credentials/shipping/easyship-credentials-store";
import KExpressCredentialsStore from "./credentials/shipping/k-express-credentials-store";
import ZigZagCredentialsStore from './credentials/shipping/zigzag-credentials-store';
import GlobalECredentialsStore from './credentials/shipping/global-e-credentials-store';
import DpdUkCredentialsStore from "./credentials/shipping/dpd-uk-credentials-store";
import UspsDirectOauthCredentialsStore from "./credentials/shipping/usps-direct-oauth-credentials-store";
import {WithOld} from "../../../../stores/types";

interface IIntegrationChanges extends Partial<IShippingConnection> {
    aggregator_code: SHIPPING_CONNECTION_CODE,
};

class ShippingIntegrationStore implements IIntegrationStore {
    private _model: IShippingConnection = {} as IShippingConnection;
    private _changes: IIntegrationChanges = {} as IIntegrationChanges;
    private _isLoading: boolean = false;
    private _isNew: boolean = false;
    private _isSandbox: boolean = false;

    public get isLoading(): boolean {
        return this._isLoading;
    }

    private _credentialsStore?: ICredentialsStore;

    public get isSandboxModeAvailable(): boolean { return this._presetValue.is_sandbox_mode_available ?? false }
    public setIsSandbox(v: boolean) { this._isSandbox = v; }
    public get isSandbox(): boolean { return this._isSandbox; }

    public get serviceCode() { return this._changes.aggregator_code ?? this._model.aggregator_code; }
    public get serviceType() { return SHIPPING_INTEGRATION_CATEGORY.SHIPPING }
    public get id() { return this._model.id; }
    public get isAllowed() { return this._presetValue.is_allowed; }
    public get isShipping() { return true; }
    public get credentialsStore() { return this._credentialsStore; }
    public get description(): { title: string, description: string, isHtml: boolean } | undefined {
        return {
            title: 'About the solution',
            description: this._presetValue.description,
            isHtml: true,
        }
    }

    public isNew(): boolean {
        return this._isNew;
    }

    public get availableSoon() { return this._presetValue.available_soon; }
    public get label() {
        if (this._model.internal_name) {
            return `${this._presetValue.label} (${this._model.internal_name})`;
        }
        return this._presetValue.label;
    }

    public get type() { return INTEGRATION_STORE_TYPE.SHIPPING; }

    public get deleteInfo(): IIntegrationDeleteInfo {
        return this._model?._delete_info || {
            can_be_deleted: true
        };
    }

    private get service(): ShippingIntegrationsService {
        return rootContainer.get<ShippingIntegrationsService>(TYPE.ShippingIntegrationsService);
    }

    constructor(private _presetValue: IIntegrationPreset, model: IShippingConnection = {} as IShippingConnection) {
        makeAutoObservable(this, {}, { autoBind: true });
        this.setModel(model);
    }

    public get internalName() {
        return this._changes.internal_name ?? this._model.internal_name;
    }

    public setInternalName(value: string) {
        this._changes.internal_name = value;
        this._clearUnneededChanges();
    }

    public setIsEnabled(v: boolean) {}

    private getUpdatedModel(): IShippingConnection {
        return { ...this._model, ...this._changes };
    }

    public hasChanges(): boolean {
        return Object.keys(this._changes).length > 0;
    }

    async forget(): Promise<void> {
        this._isLoading = true;
        try {
            if (this._model.id) {
                await this.service.removeIntegration(this._model.id);
            }
            runInAction(() => {
                this.setChanges({
                    aggregator_code: this.serviceCode,
                });
                this._model = {} as IShippingConnection;
                this._isLoading = false;
            });
        } catch (e) {
            runInAction(() => {
                this._isLoading = false;
            });
            throw e;
        }
    }

    private setModel(model: IShippingConnection = {} as IShippingConnection) {
        this._model = model;
    }

    public setChanges(changes: IIntegrationChanges): void {
        this._changes = changes;
        this.createCredentialsStore();
    }

    private discardChanges(): void {
        this._changes = {} as IIntegrationChanges;
    }

    /** credentials stores factory */
    private createCredentialsStore() {
        const errorMsg = `unknown integration ${this.serviceType}/${this.serviceCode}`;

        switch (this.serviceCode) {
            case SHIPPING_CONNECTION_CODE.SHIPPO:
            case SHIPPING_CONNECTION_CODE.SHIPPY_PRO:
            case SHIPPING_CONNECTION_CODE.PARCEL_NINJA:
            case SHIPPING_CONNECTION_CODE.EASY_POST:
            case SHIPPING_CONNECTION_CODE.SEKO:
            case SHIPPING_CONNECTION_CODE.HAPPY_RETURNS:
            case SHIPPING_CONNECTION_CODE.VESYL:
            case SHIPPING_CONNECTION_CODE.SHIPSTATION_V2:
                this._credentialsStore = new ApiKeyCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.SHIP_STATION:
            case SHIPPING_CONNECTION_CODE.SENDCLOUD:
            case SHIPPING_CONNECTION_CODE.SHIPMONDO:
            case SHIPPING_CONNECTION_CODE.STARSHIPIT:
            case SHIPPING_CONNECTION_CODE.NZ_POST_ESHIP:
            case SHIPPING_CONNECTION_CODE.USPS:
            case SHIPPING_CONNECTION_CODE.INPOST_UK:
                this._credentialsStore = new ApiSecretCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.AUSTRALIA_POST:
            case SHIPPING_CONNECTION_CODE.CARGO:
            case SHIPPING_CONNECTION_CODE.CORREOS:
                this._credentialsStore = new CustomerNumberCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.INTERSOFT:
                this._credentialsStore = new IntersoftCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.WEBSHIPPER:
                this._credentialsStore = new WebshipperCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.CANADA_POST:
                this._credentialsStore = new CanadaPostOauthCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.USPS_DIRECT_OAUTH:
                this._credentialsStore = new UspsDirectOauthCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.MONDIAL_RELAY:
                this._credentialsStore = new MondialRelayCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.REBOUND:
                this._credentialsStore = new ReboundCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.DHL_EXPRESS:
                this._credentialsStore = new DhlCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.SENDCLOUD_RETURNS:
                this._credentialsStore = new SendcloudReturnsCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.FEDEX:
                this._credentialsStore = new FedexCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.FEDEX_V2:
                this._credentialsStore = new FedexV2CredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.THE_RETURN:
                this._credentialsStore = new NoCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.ENVOI_DU_NET:
                this._credentialsStore = new EnvoiDuNetCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.LABELGO:
                this._credentialsStore = new LabelGOCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.YODEL:
                this._credentialsStore = new YodelCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.SALASA:
                this._credentialsStore = new SalasaCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.DHL_DE_RETURNS:
                this._credentialsStore = new DhlDeReturnsCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.EASYSHIP:
                this._credentialsStore = new EasyshipCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.K_EXPRESS:
                this._credentialsStore = new KExpressCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.ZIGZAG:
                this._credentialsStore = new ZigZagCredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.GLOBAL_E:
                this._credentialsStore = new GlobalECredentialsStore();
                break;
            case SHIPPING_CONNECTION_CODE.DPD_UK:
                this._credentialsStore = new DpdUkCredentialsStore();
                break;
            default:
                throw new Error(errorMsg);
        }
    }

    public async register(): Promise<void> {
        this._isNew = true;
        this._isLoading = true;
        try {
            const credentials = await this.credentialsStore?.getCredentials(this.isSandbox);
            const integration = await this.service.registerIntegration(
                credentials,
                this.serviceCode,
                this.serviceType,
                this.isSandbox,
            );
            runInAction(() => {
                this.setModel(integration);
                this._isLoading = false;
                this.discardChanges();
            });
        } catch (e: any) {
            runInAction(() => {
                this._isLoading = false;
            });
            console.error(`ShippingIntegrationStore.register: ${e}`);
        }
    }

    public async update(): Promise<void> {
        this._isLoading = true;

        const updatedModel: WithOld<IShippingConnection> = this.getUpdatedModel();
        updatedModel.__old = this._model;

        let updatedIntegration: IShippingConnection | undefined = undefined;
        try {
            updatedIntegration = await this.service.updateIntegration(updatedModel.id, updatedModel);
        } catch (e) {
            console.error(`ShippingIntegrationStore.update: ${e}`);
            runInAction(() => {
                this._isLoading = false;
            });
        }

        runInAction(() => {
            if (updatedIntegration) {
                this.setModel(updatedIntegration);
            }
            this._isLoading = false;
            this.discardChanges();
        });
    }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof IShippingConnection;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }
}

export default ShippingIntegrationStore;
